import { useForm } from '@mantine/form'
import { useDispatch } from 'react-redux'
import { SuccessNotification } from 'app/views/components/notifications/notification'
import { createUser, updateUser } from 'app/state/ducks/users/thunks'
import UserFormPresentation from './user-form-presentation'

export type Role = 'Admin' | 'Manager' | 'Admin and Manager'
export type AccessLevel = 'ViewOnly' | 'Create'
export type Status = 'Enabled' | 'Disabled'

export interface SavedUser {
  createdAt: string
  id: string
  firstName: string
  lastName: string
  email: string
  username: string
  phoneNumber: string | null
  role: Role | null
  accessLevel: AccessLevel | null
  status: Status
  company: string
  region: string | null
}

export interface UserFormValues {
  id: string
  firstName: string
  lastName: string
  email: string
  username: string
  phoneNumber: string
  role: Role | null
  accessLevel: AccessLevel | null
  company: string
  status: Status | null
  region: string | null
  createdAt: string
}

export type UserFormMode = 'create' | 'edit'

export type SaveResult = { success: true } | { success: false; errorMessage: string }

type Props = {
  close: () => void
  user?: UserFormValues
  mode?: UserFormMode
  onSaved?: (user: SavedUser) => void
}

export default function UserFormLogic({ close, user, mode, onSaved }: Props) {
  const dispatch = useDispatch()
  const resolvedMode: UserFormMode = mode ?? (user ? 'edit' : 'create')
  const isEdit = resolvedMode === 'edit'

  const form = useForm<UserFormValues>({
    initialValues: {
      id: user?.id ?? '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      username: user?.username ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      role: user?.role ?? null,
      accessLevel: user?.accessLevel ?? null,
      company: user?.company ?? '',
      status: user?.status ?? null,
      region: user?.region ?? null,
      createdAt: user?.createdAt ?? '',
    },
    validate: {
      firstName: (value) => (value.trim() ? null : 'First name is required'),
      lastName: (value) => (value.trim() ? null : 'Last name is required'),
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : 'Invalid email',
      username: (value) => (value.trim() ? null : 'Username is required'),
      role: (value) => (value ? null : 'Role is required'),
      accessLevel: (value) => (value ? null : 'Access level is required'),
      company: (value) => (value.trim() ? null : 'Company is required'),
      status: (value) => (value ? null : 'Status is required'),
    },
    validateInputOnBlur: true,
  })

  const onSave = async (): Promise<SaveResult> => {
    try {
      const user = {
        firstName: form.values.firstName,
        lastName: form.values.lastName,
        email: form.values.email,
        username: form.values.username,
        phoneNumber: form.values.phoneNumber,
        role: form.values.role as Role,
        accessLevel: form.values.accessLevel as AccessLevel,
        status: form.values.status as Status,
        company: form.values.company,
        region: form.values.region ?? null,
      }
      const thunk = isEdit ? updateUser(form.values.id, user) : createUser(user)
      const response: any = await (dispatch(thunk) as unknown as Promise<{ success: boolean; payload?: unknown }>)

      if (response.success) {
        SuccessNotification({
          title: isEdit ? 'User Updated' : 'User Created',
          message: isEdit
            ? 'User has been updated successfully'
            : 'User has been created successfully',
        })
        onSaved?.(response.payload as SavedUser)
        return { success: true }
      }
      return {
        success: false,
        errorMessage: response.payload ?? 'Please check inputs and try again',
      }
    } catch {
      return {
        success: false,
        errorMessage: 'Please check inputs and try again',
      }
    }
  }

  return (
    <UserFormPresentation
      form={form}
      close={close}
      isEdit={isEdit}
      onSave={onSave}
    />
  )
}
